import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { ChannelConfig, ChannelType, ChannelStatus, ChannelMeta } from '../../types/messaging.js';
import { CHANNEL_META } from '../../types/messaging.js';
import { wsClient } from '../../services/websocket-client.js';
import * as messagingApi from '../../services/messaging-api.js';

interface WebSocketMessage<T = unknown> {
  type: string;
  payload: T;
  timestamp: string;
}

interface ChannelStatusPayload {
  channelType: ChannelType;
  channelConfigId?: number;
  error?: string;
}

const CHANNEL_TYPES: ChannelType[] = ['telegram', 'slack', 'whatsapp'];

@customElement('cp-company-channel-widget')
export class CpCompanyChannelWidget extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Number }) companyId = 0;

  @state() private _channels: ChannelConfig[] = [];
  @state() private _loading = false;
  @state() private _error = '';
  @state() private _expandedId: number | null = null;

  // Add form state
  @state() private _showForm = false;
  @state() private _formType: ChannelType = 'telegram';
  @state() private _formName = '';
  @state() private _formCredentials = '';
  @state() private _saving = false;

  // Edit state
  @state() private _editCredentials = '';
  @state() private _editName = '';
  @state() private _testing = false;
  @state() private _deleting = false;
  @state() private _successMessage = '';

  private _boundWsHandler = this._onWsMessage.bind(this);

  override async connectedCallback(): Promise<void> {
    super.connectedCallback();
    wsClient.addEventListener('ws-message', this._boundWsHandler);
    await this._load();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    wsClient.removeEventListener('ws-message', this._boundWsHandler);
  }

  private async _load(): Promise<void> {
    if (!this.companyId) return;
    this._loading = true;
    this._error = '';
    try {
      this._channels = await messagingApi.getChannelsByCompany(this.companyId);
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Laden';
    } finally {
      this._loading = false;
    }
  }

  private _onWsMessage(e: Event): void {
    const { detail } = e as CustomEvent<WebSocketMessage>;
    switch (detail.type) {
      case 'messaging:channel-connected': {
        const payload = detail.payload as ChannelStatusPayload;
        this._updateStatus(payload.channelConfigId, payload.channelType, 'connected');
        break;
      }
      case 'messaging:channel-disconnected': {
        const payload = detail.payload as ChannelStatusPayload;
        this._updateStatus(payload.channelConfigId, payload.channelType, 'disconnected');
        break;
      }
      case 'messaging:channel-error': {
        const payload = detail.payload as ChannelStatusPayload;
        this._updateStatus(payload.channelConfigId, payload.channelType, 'error', payload.error);
        break;
      }
    }
  }

  private _updateStatus(channelConfigId: number | undefined, channelType: ChannelType, status: ChannelStatus, errorMessage?: string): void {
    this._channels = this._channels.map((ch) => {
      if (channelConfigId != null && ch.id === channelConfigId) {
        return { ...ch, status, error_message: errorMessage ?? ch.error_message };
      }
      if (channelConfigId == null && ch.channel_type === channelType) {
        return { ...ch, status, error_message: errorMessage ?? ch.error_message };
      }
      return ch;
    });
  }

  private _getStatusLabel(status: ChannelStatus): string {
    switch (status) {
      case 'connected': return 'Verbunden';
      case 'connecting': return 'Verbindet...';
      case 'disconnected': return 'Getrennt';
      case 'error': return 'Fehler';
    }
  }

  private _getStatusClass(status: ChannelStatus): string {
    switch (status) {
      case 'connected': return 'cp-company-channels__status--connected';
      case 'connecting': return 'cp-company-channels__status--connecting';
      case 'disconnected': return 'cp-company-channels__status--disconnected';
      case 'error': return 'cp-company-channels__status--error';
    }
  }

  private _toggleExpand(id: number): void {
    if (this._expandedId === id) {
      this._expandedId = null;
      this._successMessage = '';
      return;
    }
    this._expandedId = id;
    this._error = '';
    this._successMessage = '';
    const ch = this._channels.find((c) => c.id === id);
    if (ch) {
      this._editName = ch.display_name;
      this._editCredentials = '';
    }
  }

  // --- Add form ---
  private _startAdd(): void {
    this._showForm = true;
    this._formType = 'telegram';
    this._formName = '';
    this._formCredentials = '';
    this._error = '';
    this._successMessage = '';
  }

  private _cancelForm(): void {
    this._showForm = false;
    this._formName = '';
    this._formCredentials = '';
    this._error = '';
  }

  private async _saveNew(): Promise<void> {
    const meta = CHANNEL_META[this._formType];
    if (!this._formCredentials.trim()) {
      this._error = `${meta.credentialLabel} ist erforderlich`;
      return;
    }
    this._saving = true;
    this._error = '';
    try {
      const created = await messagingApi.createChannel({
        channel_type: this._formType,
        display_name: this._formName.trim() || meta.label,
        credentials: this._formCredentials.trim(),
        company_id: this.companyId,
      });
      this._channels = [...this._channels, created];
      this._cancelForm();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Erstellen';
    } finally {
      this._saving = false;
    }
  }

  // --- Edit / Test / Delete ---
  private async _saveEdit(ch: ChannelConfig): Promise<void> {
    this._saving = true;
    this._error = '';
    this._successMessage = '';
    try {
      const updateData: Record<string, string> = {};
      if (this._editName.trim()) updateData.display_name = this._editName.trim();
      if (this._editCredentials.trim()) updateData.credentials = this._editCredentials.trim();
      const updated = await messagingApi.updateChannel(ch.id, updateData);
      this._channels = this._channels.map((c) => c.id === updated.id ? updated : c);
      this._editCredentials = '';
      this._successMessage = 'Aktualisiert';
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Speichern';
    } finally {
      this._saving = false;
    }
  }

  private async _test(ch: ChannelConfig): Promise<void> {
    this._testing = true;
    this._error = '';
    this._successMessage = '';
    try {
      const result = await messagingApi.testChannel(ch.id);
      if (result.success) {
        this._successMessage = 'Verbindung erfolgreich';
        this._channels = this._channels.map((c) =>
          c.id === ch.id ? { ...c, status: result.status ?? 'connected' as ChannelStatus } : c
        );
      } else {
        this._error = result.message ?? 'Verbindungstest fehlgeschlagen';
      }
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Testen';
    } finally {
      this._testing = false;
    }
  }

  private async _delete(ch: ChannelConfig): Promise<void> {
    this._deleting = true;
    this._error = '';
    try {
      await messagingApi.deleteChannel(ch.id);
      this._channels = this._channels.filter((c) => c.id !== ch.id);
      this._expandedId = null;
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Loeschen';
    } finally {
      this._deleting = false;
    }
  }

  // --- Rendering ---
  private _renderChannel(ch: ChannelConfig) {
    const meta: ChannelMeta = CHANNEL_META[ch.channel_type];
    const isExpanded = this._expandedId === ch.id;

    return html`
      <div class="cp-company-channels__item ${isExpanded ? 'cp-company-channels__item--expanded' : ''}">
        <button class="cp-company-channels__item-header" @click=${() => this._toggleExpand(ch.id)}>
          <span class="cp-company-channels__item-icon">${meta.icon}</span>
          <span class="cp-company-channels__item-name">${ch.display_name}</span>
          <span class="cp-company-channels__status ${this._getStatusClass(ch.status)}">
            ${this._getStatusLabel(ch.status)}
          </span>
          <span class="cp-company-channels__chevron">${isExpanded ? '▾' : '▸'}</span>
        </button>
        ${isExpanded ? html`
          <div class="cp-company-channels__item-body">
            ${ch.error_message ? html`
              <div class="cp-company-channels__msg cp-company-channels__msg--error">${ch.error_message}</div>
            ` : nothing}
            ${this._successMessage ? html`
              <div class="cp-company-channels__msg cp-company-channels__msg--success">${this._successMessage}</div>
            ` : nothing}

            <div class="cp-company-channels__field">
              <label class="cp-company-channels__label">Anzeigename</label>
              <input
                class="cp-company-channels__input"
                type="text"
                .value=${this._editName}
                @input=${(e: Event) => { this._editName = (e.target as HTMLInputElement).value; }}
              />
            </div>

            <div class="cp-company-channels__field">
              <label class="cp-company-channels__label">${meta.credentialLabel}</label>
              <input
                class="cp-company-channels__input cp-company-channels__input--mono"
                type="password"
                placeholder="Leer lassen um beizubehalten"
                .value=${this._editCredentials}
                @input=${(e: Event) => { this._editCredentials = (e.target as HTMLInputElement).value; }}
              />
              <span class="cp-company-channels__hint">${meta.credentialHint}</span>
            </div>

            <div class="cp-company-channels__actions">
              <button
                class="cp-company-channels__btn cp-company-channels__btn--save"
                @click=${() => this._saveEdit(ch)}
                ?disabled=${this._saving}
              >${this._saving ? 'Speichert...' : 'Speichern'}</button>
              <button
                class="cp-company-channels__btn cp-company-channels__btn--test"
                @click=${() => this._test(ch)}
                ?disabled=${this._testing}
              >${this._testing ? 'Testet...' : 'Testen'}</button>
              <button
                class="cp-company-channels__btn cp-company-channels__btn--delete"
                @click=${() => this._delete(ch)}
                ?disabled=${this._deleting}
              >${this._deleting ? 'Loescht...' : 'Entfernen'}</button>
            </div>
          </div>
        ` : nothing}
      </div>
    `;
  }

  private _renderAddForm() {
    if (!this._showForm) return nothing;
    const meta = CHANNEL_META[this._formType];

    return html`
      <div class="cp-company-channels__form">
        <div class="cp-company-channels__field">
          <label class="cp-company-channels__label">Typ</label>
          <select
            class="cp-company-channels__select"
            .value=${this._formType}
            @change=${(e: Event) => { this._formType = (e.target as HTMLSelectElement).value as ChannelType; }}
          >
            ${CHANNEL_TYPES.map((t) => html`
              <option value=${t}>${CHANNEL_META[t].label}</option>
            `)}
          </select>
        </div>

        <div class="cp-company-channels__field">
          <label class="cp-company-channels__label">Anzeigename</label>
          <input
            class="cp-company-channels__input"
            type="text"
            placeholder=${meta.label}
            .value=${this._formName}
            @input=${(e: Event) => { this._formName = (e.target as HTMLInputElement).value; }}
          />
        </div>

        <div class="cp-company-channels__field">
          <label class="cp-company-channels__label">${meta.credentialLabel}</label>
          <input
            class="cp-company-channels__input cp-company-channels__input--mono"
            type="password"
            placeholder=${meta.credentialPlaceholder}
            .value=${this._formCredentials}
            @input=${(e: Event) => { this._formCredentials = (e.target as HTMLInputElement).value; }}
          />
          <span class="cp-company-channels__hint">${meta.credentialHint}</span>
        </div>

        <div class="cp-company-channels__form-actions">
          <button
            class="cp-company-channels__btn cp-company-channels__btn--save"
            @click=${this._saveNew}
            ?disabled=${this._saving}
          >${this._saving ? 'Erstellt...' : 'Erstellen'}</button>
          <button
            class="cp-company-channels__btn cp-company-channels__btn--cancel"
            @click=${this._cancelForm}
          >Abbrechen</button>
        </div>
      </div>
    `;
  }

  override render() {
    if (this._loading) {
      return html`<p class="cp-company-channels__loading">Laden...</p>`;
    }

    return html`
      <div class="cp-company-channels">
        ${this._error ? html`
          <p class="cp-company-channels__msg cp-company-channels__msg--error">${this._error}</p>
        ` : nothing}

        ${this._channels.length === 0 && !this._showForm ? html`
          <p class="cp-company-channels__empty">Keine Messaging Bots konfiguriert</p>
        ` : nothing}

        ${this._channels.length > 0 ? html`
          <div class="cp-company-channels__list">
            ${this._channels.map((ch) => this._renderChannel(ch))}
          </div>
        ` : nothing}

        ${this._renderAddForm()}

        ${!this._showForm ? html`
          <div class="cp-company-channels__add">
            <button class="cp-company-channels__action-link" @click=${this._startAdd}>
              + Bot hinzufuegen
            </button>
          </div>
        ` : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-company-channel-widget': CpCompanyChannelWidget;
  }
}
